import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { CommentThreadItemFragment } from '../../api/client'
import { isUserAdminState } from '../../store/DomainStore'

type Props = {
    post: NonNullable<CommentThreadItemFragment['post']>
    // Names the platform on the external link ("Open on Instagram").
    platformName: string
    // The Arwen account that owns the post. The post itself doesn't carry it,
    // so the drawer reads it off the thread's comments.
    accountName?: string | null
}

/**
 * The post a thread hangs off, set as the drawer's heading on its dark band.
 * Every comment below is a reply to it, so it leads rather than sitting in the
 * list as a callout. Hidden by the drawer when Arwen doesn't hold the post.
 */
export default function ConversationPostHeader({ post, platformName, accountName }: Props) {
    const admin = useRecoilValue(isUserAdminState)
    return (
        <div className="mt-3 flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-x-1.5 text-sm text-ink-300">
                {accountName && (
                    <>
                        <span className="font-medium text-white">{accountName}</span>
                        <span aria-hidden="true">·</span>
                    </>
                )}
                <span>{dayjs(post.publishTime).format('ddd D MMM, HH:mm')}</span>
            </div>
            <h2 className="line-clamp-4 text-lg font-medium leading-snug text-white">
                {post.title}
            </h2>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                {/* The post page lives under /admin, so only admins get the link to it.
                    It shows every comment on the post, where the drawer shows one thread. */}
                {admin && (
                    <Link
                        to={`/admin/post/${post.id}`}
                        className="text-arwen-300 underline hover:text-arwen-200"
                    >
                        View post
                    </Link>
                )}
                <a
                    href={post.uri}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-arwen-300 underline hover:text-arwen-200"
                >
                    Open on {platformName}
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
            </div>
        </div>
    )
}
